export const AMC_CONTENT = {
  TITLE: 'Actively Managed Certificates (AMCs) at MarQ Capital',
  INTRO:
    'At MarQ Capital, we use Actively Managed Certificates (AMCs) as a strategic vehicle for distributing our investment strategies. This structure allows us to provide qualified investors with direct access to sophisticated, professionally managed portfolios with high levels of transparency and flexibility. Our first AMC was launched in August 2025, featuring the StrandQ strategy, marking a significant milestone in expanding our institutional offering.',
  UNDERSTANDING:
    'AMCs are innovative financial instruments that offer exposure to actively managed portfolios. Structured as debt securities issued by financial institutions or dedicated vehicles, AMCs replicate the performance of a discretionary investment strategy, which may include equities, bonds, and alternative assets.\n\nThe key advantage of AMCs lies in active management, where a professional manager makes tactical decisions about asset allocation, with the goal of outperforming passive investment strategies. This enables the portfolio to adapt more effectively to changing market conditions.\n\nIn addition, AMCs offer flexibility and dynamic asset allocation, along with continuous transparency, through regular performance updates and detailed reporting. This structure provides an efficient solution for investors seeking a professional and agile investment approach, without the regulatory burden of traditional fund vehicles.',
  FEATURES_LIST: {
    TITLE: 'Key Features and Benefits',
    LIST_ITEMS: [
      {
        TITLE: 'Active and dynamic management',
        TEXT: 'The manager adjusts the portfolio based on market conditions to optimize risk-adjusted returns.'
      },
      {
        TITLE: 'Broad diversification',
        TEXT: 'Exposure to a wide range of assets, from equities and bonds to alternative strategies, within a single structure.'
      },
      {
        TITLE: 'Cost efficiency',
        TEXT: 'AMCs typically have lower management fees compared to traditional mutual funds or hedge funds, while still providing access to advanced strategies.'
      },
      {
        TITLE: 'Transparency and reporting',
        TEXT: 'Investors benefit from regular updates and clear visibility into portfolio performance.'
      },
      {
        TITLE: 'Speed and flexibility',
        TEXT: 'AMCs enable fast deployment of strategies in the market and can be tailored to different investor profiles and jurisdictions.'
      }
    ]
  },
  WHO_FOR:
    'AMCs are particularly suited for institutional and qualified investors seeking access to actively managed strategies with a high degree of flexibility and diversification. They are ideal for those who want to navigate market cycles more proactively, benefiting from dynamic portfolio allocation and professional oversight.',
  FOOTER:
    'If you would like to learn more about our StrandQ AMC, please get in touch with our team.'
};
/**
 * Content for the Strategies pages
 */
export const STRATEGIES_CONTENT = {
  TITLE: 'Strategies',
  SUBTITLE:
    "MarQ Capital's systematic programs are designed to capture opportunities in liquid global markets while maintaining low correlation to traditional asset classes. Each strategy applies a disciplined, rules-based framework that adapts to volatility regimes and seeks to complement existing allocations.",
  SECTIONS: {
    TECHNOLOGY: {
      TITLE: 'Technology',
      TEXT: [
        'Technology is central to MarQ Capital. Our engineers build proprietary systems tailored to our performance and security needs.',
        'When standard tools fall short, we develop our own like Spinnaker Algo Systems LTD, launched in 2022 to serve both MarQ Capital and external managers.',
        'Our innovative and entrepreneurial mindset keeps us ahead in finance and technology'
      ],
      LINKS: [
        {
          word: 'Spinnaker Algo Systems LTD',
          url: 'https://www.spinnakeralgo.com/',
          color: 'var(--primary-blue)',
          textDecoration: 'underlined',
          newTab: true
        }
      ]
    },
    PROGRAMS: {
      TITLE: 'Programs',
      STRAND_Q: {
        TITLE: 'StrandQ',
        LABEL: 'EUR/USD FX',
        BULLET_POINTS: [
          'Systematic counter-trend and trend-following EUR/USD strategy',
          'Daily liquidity via Luxembourg AMC structure',
          'Conservative capacity: 200M USD'
        ],
        LINK: '/strategies/strand-q'
      },
      MERIN: {
        TITLE: 'Merin',
        LABEL: 'G7 FX Universe',
        BULLET_POINTS: [
          'Multi-currency systematic FX strategy across G7 pairs',
          'Low volatility, uncorrelated alpha generation',
          // 'Optimized for institutional execution',
          'Conservative capacity: 500M USD'
        ],
        LINK: '/strategies/merin'
      },
      DRAVON: {
        TITLE: 'Dravon',
        LABEL: 'Commoditie',
        BULLET_POINTS: [
          'Gold coverage',
          'Volatility challenger',
          'Deep market adaptability with robust risk management',
          'Conservative capacity: 150M USD'
        ],
        LINK: '/strategies/dravon'
      },
      GLOBAL_MACRO: {
        TITLE: 'Global Macro',
        LABEL: 'Equity Indices',
        BULLET_POINTS: [
          'S&P 500, DAX 40, Euro Stoxx 50, Nasdaq 100, FTSE, ASX 200, Nikkei 225',
          'Systematic index allocation with macro overlays',
          'Currently in incubation phase'
        ],
        LINK: '/strategies/global-macro'
      },
      COMPARISON_TABLE: {
        HEADERS: ['Feature', 'StrandQ', 'Merin', 'Dravon', 'Global Macro'],
        ROWS: [
          {
            FEATURE: 'Asset Coverage',
            VALUES: ['EUR/USD', 'G7 FX', 'Gold', 'Equity Indices']
          },
          {
            FEATURE: 'Structure',
            VALUES: ['AMC / SMA', 'AMC / SMA', 'AMC / SMA', 'AMC / SMA']
          },
          {
            FEATURE: 'Liquidity',
            VALUES: ['Daily (T+2)', 'Daily (T+2)', 'Daily (T+2)', 'Daily (T+2)']
          },
          {
            FEATURE: 'Min Ticket Size',
            VALUES: ['100,000', '100,000', '100,000', '100,000']
          },
          {
            FEATURE: 'Leverage',
            VALUES: ['2:1 / 2.5:1', '6:1', '4:1 / 3:1', '1:1']
          }
        ]
      },
      FLOW_STEPS: ['Investor', 'AMC / SMA', 'Custodian', 'Markets']
    },
    SWINGVISION: {
      TITLE: 'SwingVision – Our Core Strategy Engine',
      TEXT: [
        'SwingVision is the proprietary quantitative engine that powers all MarQ Capital strategies. It combines behavioral pattern recognition, volatility cycle analysis, and multi-timeframe technical logic to generate systematic trading signals across asset classes.'
      ],
      FEATURES: [
        'Fully systematic and rules-based: no discretionary intervention',
        'Adaptive to changing volatility regimes and market conditions',
        'Real-time monitoring via Sentinel platform for institutional transparency',
        'Designed for low correlation to traditional equity and fixed-income markets'
      ],
      FLOW_STEPS: ['Models', 'SwingVision Engine', 'Programs', 'Investors']
    },
    ACCESSING: {
      TITLE: 'Accessing Our Strategies',
      INTRO:
        'MarQ Capital strategies are accessible through two primary structures, both designed for institutional allocation:',
      AMC: {
        TITLE: 'Actively Managed Certificates (AMC)',
        TEXT: 'Luxembourg-domiciled, bankruptcy-remote SPV structure. Subscribed via ISIN through prime brokers and international custodians. Daily NAV calculation, T+2 settlement.'
      },
      SMA: {
        TITLE: 'Separately Managed Accounts (SMA)',
        TEXT: 'Direct execution via regulated broker platforms. Client retains full custody and control. Real-time signal delivery with institutional oversight.'
      },
      TRANSPARENCY:
        'Both structures offer full transparency through our Sentinel monitoring platform, allowing investors to track performance, positions, and risk metrics in real time.',
      RISK_MANAGEMENT: [
        {
          TITLE: 'Strict Risk Limits',
          TEXT: 'Maximum 10% drawdown limit based on High-Water Mark, monitored in real time'
        },
        {
          TITLE: 'Automated Circuit Breakers',
          TEXT: 'Real-time monitoring with automatic suspension rules if thresholds are breached'
        },
        {
          TITLE: 'Daily Oversight',
          TEXT: 'Risk team reviews exposure, positions, and market conditions daily'
        },
        {
          TITLE: 'Sentinel Platform',
          TEXT: 'Proprietary risk management software providing institutional-grade transparency and real-time alerts'
        },
        {
          TITLE: 'Segregated Custody',
          TEXT: 'All assets held in client-segregated accounts, ensuring no cashflow volatility risk from other investors'
        }
      ]
    },
    FOOTER_NOTE:
      'This information has been prepared solely for informational purposes and is not an offer to buy or sell or a solicitation of an offer to buy or sell any security or instrument or to participate in any trading strategy. Past performance is not necessarily indicative of future results.'
  }
};

export const INVESTMENT_PROGRAMS_CONTENT = {
  TITLE: 'Quantitative Strategies',
  INTRO: [
    'Our quantitative strategies combine systematic strategies, technology and disciplined execution to deliver risk-adjusted returns. Each one reflects our focus on research, innovation and long-term value.',
    'We are gradually rolling out new strategies, but our current flagship is Strand Q, focused on FX markets.'
  ],
  PROGRAMS: {
    STRAND_Q: {
      TITLE: 'Strand Q: Flagship Systematic Strategy',
      DESCRIPTION:
        'The central objective of this quantitative strategy in FX is to capitalise on exchange rate fluctuations between EUR and USD.',
      LAUNCH_DATE: 'Launched: January 2024',
      INSTRUMENTS_TRADED: 'Instruments Traded: EUR/USD (Spot & CFDs)'
    },
    MERIN: {
      TITLE: 'Merin',
      DESCRIPTION:
        'Focused on the systematic trading of the seven major global currencies.',
      LAUNCH_DATE: 'Launched: June 2025',
      INSTRUMENTS_TRADED:
        'Instruments Traded: EUR / GBP / JPY / CHF / AUD / CAD / NZD (Spot & CFDs)'
    },
    DRAVON: {
      TITLE: 'Dravon',
      DESCRIPTION:
        'Reflects macro and behavioural sentiment across different markets.',
      LAUNCH_DATE: 'Launched: June 2025',
      INSTRUMENTS_TRADED:
        'Instruments Traded: Crude / Gold / S&P500 / EURUSD (Spot & CFDs)'
    },
    GLOBAL_MACRO: {
      TITLE: 'Global Macro: In Preparation',
      DESCRIPTION:
        'Multi-asset strategy systematically allocating risk across SP500.',
      LAUNCH_DATE: 'Launched: N/A',
      INSTRUMENTS_TRADED: 'Instruments Traded: stocks of SP500.'
    }
  },
  PROGRAM_DETAILS_LEFT: [
    { TEXT: 'Strategy Focus: Systematic, short-to-medium-term' },
    { TEXT: 'Holding Period: Average 1–2 days' },
    {
      TEXT: 'Format: Separately managed account (SMA) and by AMC'
    }
  ],
  PROGRAM_DETAILS_RIGHT: [
    {
      TEXT: 'Objective: Alpha through trend participation and volatility models'
    },
    {
      TEXT: 'Risk Management: Multi-layered controls with continuous monitoring'
    },
    { TEXT: 'Liquidity - Daily' }
  ],
  FUTURE_OFFERINGS: {
    TITLE: 'Future Offerings',
    DESCRIPTION:
      "We're developing new strategies across other asset classes, focusing on scalability, low correlation, and adaptability all built on the same systematic foundation as Strand Q."
  },
  CUSTOM_SOLUTIONS: {
    TITLE: 'Custom Solutions',
    TEXT: 'We create tailored portfolios for institutional investors, aligned with specific objectives, mandates, and constraints.',
    HIGHLIGHT:
      'Contact us to explore bespoke strategies and build solutions that meet your goals.'
  },
  FOOTER_NOTE:
    'This information has been prepared solely for informational purposes and is not an offer to buy or sell or a solicitation of an offer to buy or sell any security or instrument or to participate in any trading strategy. Past performance is not necessarily indicative of future results.'
};

export const TRADING_BUSINESS_CONTENT = {
  TITLE: 'Trading Business Vision',
  INTRO: [
    'At MarQ Capital, we aim to trade seamlessly across all liquid, electronically tradable asset classes and time horizons, delivering consistent returns on both proprietary and client capital.',
    'Our modular, scalable technology supports a wide range of strategies, enabling a versatile and adaptive approach to evolving market conditions.'
  ],
  SECTIONS: {
    PEOPLE_CORE: {
      TITLE: 'People at the Core',
      TEXT: 'We automate repetitive tasks to let our people focus on creating, innovating, and collaborating. We foster a culture where every contribution has real impact.'
    },
    PURPOSE_DRIVEN: {
      TITLE: 'Purpose-Driven Opportunity',
      TEXT: 'Maximising opportunity means focusing on long-term growth and resilience, not short-term gains. At MarQ Capital, consistency and sustainability guide our strategies.'
    },
    AUTOMATION: {
      TITLE: 'Automation as a Catalyst for Innovation',
      TEXT: [
        'Automation is a strategic enabler that frees us to focus on high-impact work. By streamlining tasks, we unlock time for innovation and business growth.'
      ]
    },
    LONG_TERM: {
      TITLE: 'Built with a Long-Term Mindset',
      TEXT: 'Everything we develop whether technology or internal processes is built with durability, excellence, and scalability in mind. We aim for perfection in the details, creating a foundation for sustainable and scalable growth.'
    },
    OUR_PRINCIPLES: {
      TITLE: 'Our Principles: A Foundation for Sustainable Growth',
      TEXT: [
        'Our principles guide every decision at MarQ Capital. They shape how we work, build trust, and promote a culture of integrity, responsibility, and transparency driving both performance and long-term growth.'
      ]
    },
    PRINCIPLES: [
      {
        TITLE: 'Exceptional People, Strong Culture',
        TEXT: [
          'We invest in top talent who align with our values and bring technical excellence. We create an environment where people feel proud to belong, grow, and make a real impact.'
        ]
      },
      {
        TITLE: 'Humility, Respect & Trust',
        TEXT: [
          'We listen, learn, and lead with empathy. A culture of constructive feedback and mutual respect helps us grow stronger together.'
        ]
      },
      {
        TITLE: 'Entrepreneurial Mindset',
        TEXT: [
          'We think like owners testing ideas, learning from failure, and building for the long term. Every improvement counts toward sustainable success.'
        ]
      },
      {
        TITLE: 'A Collaborative Spirit',
        TEXT: [
          'We believe in collective intelligence. By sharing ideas and working as one team, we move faster, solve better, and build stronger.'
        ]
      }
    ]
  },
  FOOTER_NOTE:
    "This vision statement reflects MarQ Capital's ongoing commitment to excellence, innovation, and long-term strategic growth through technology and human potential."
};

export const STRAND_Q_CONTENT = {
  TITLE: 'Smart Risk Management in EURUSD',
  OVERVIEW: [
    'EURUSD is the most traded currency pair in the world and also one of the most sensitive to macroeconomic shifts, geopolitical events, and central bank decisions. Strand Q, developed by MarQ Capital, offers a systematic and resilient approach to capturing movements in this pair, with a strong focus on risk management and capital preservation.',
    'Despite its liquidity and depth, the FX market remains heavily influenced by human decisions, emotional reactions, and unpredictable volatility patterns. Ignoring the EURUSD opportunity can mean missing out on consistent returns in one of the most efficient and liquid markets globally.'
  ],
  WHY_EURUSD: {
    TITLE: 'Why EURUSD?',
    ITEMS: [
      {
        TEXT: "**High liquidity**: As the world's most transacted currency pair, EURUSD offers efficient execution and low transaction costs ideal for systematic strategies with disciplined order execution."
      },
      {
        TEXT: '**Smart diversification**: Even within equity-focused or multi-asset portfolios, incorporating a FX strategy like Strand Q provides valuable diversification, with low correlation to traditional assets like the S&P 500 or bonds.'
      },
      {
        TEXT: '**Transparent and regulated environment**: With strong oversight from central banks and broad media coverage, EURUSD is one of the most predictable pairs in terms of macro response.'
      }
    ]
  },
  CHALLENGES: {
    TITLE: 'Challenges of trading EURUSD',
    ITEMS: [
      {
        TEXT: '**Macroeconomic volatility**: ECB and Fed decisions, inflation data, and job reports often trigger sharp, unexpected moves.'
      },
      {
        TEXT: '**Technical traps**: EURUSD is followed by millions of traders and algorithms, creating liquidity traps and short-term moves that challenge less robust strategies.'
      }
    ]
  },
  SOLUTION: {
    TITLE: 'The Strand Q Solution',
    INTRO:
      'By combining deep learning with a risk-adjusted trading framework, Strand Q was developed to operate exclusively on EURUSD using a fully automated decision system. It detects price behavior patterns and adjusts market exposure based on implied volatility and dominant macroeconomic cycles.',
    ITEMS: [
      {
        TEXT: '**Active risk reduction**: The strategy is built with capital protection in mind. In adverse scenarios, the system reduces exposure and executes hedging or partial liquidations.'
      },
      {
        TEXT: '**Quantitative approach**: The process is 100% systematic, with no discretionary intervention. Thousands of data points are analyzed to define optimal positioning, with emphasis on reversal zones, volatility compression, and breakouts.'
      },
      {
        TEXT: '**Dynamic adaptation**: The learning algorithm is continuously updated with new data, allowing it to quickly detect structural shifts in EURUSD behavior.'
      }
    ]
  },
  OBJECTIVE: {
    TITLE: 'Objective?',
    TEXT: 'Strand Q aims to deliver consistent returns with reduced drawdowns even during periods of uncertainty or market stress. Its strength becomes most evident during corrections or market dysfunction, offering investors a disciplined and resilient solution for FX exposure.'
  },
  FOOTER_NOTE:
    'MarQ Capital’s systematic investment programs are designed for transparency, performance, and partnership. For more information or to discuss a tailored solution, please reach out to our team.'
};

export const MERIN_CONTENT = {
  TITLE: 'Multi-Currency Strength, One Strategy',
  OVERVIEW: [
    "The world's major currencies, including USD, EUR, GBP, JPY, CHF, CAD, AUD and NZD, form the backbone of global finance. While highly liquid and widely traded, these currencies are subject to distinct economic cycles, monetary policies, and geopolitical shifts. Merin, developed by MarQ Capital, is a systematic, multi-currency strategy designed to harness the opportunities across this elite group while maintaining tight control over portfolio risk.",
    'Unlike single-currency strategies that rely on a narrow macro view, Merin takes a global, adaptive approach dynamically positioning across the majors based on volatility signals, macroeconomic divergence, and technical behavior.'
  ],
  WHY_MAJOR_CURRENCIES: {
    TITLE: 'Why Major Currencies?',
    ITEMS: [
      {
        TEXT: '**Diversification across regimes**: Each of the 7 major currencies is influenced by different central banks, inflation paths, and growth trends, providing natural diversification.'
      },
      {
        TEXT: '**Liquidity and transparency**: These are the most liquid instruments in FX, ensuring tight spreads, reliable execution, and reduced slippage.'
      },
      {
        TEXT: '**Macro-driven opportunities**: Major currencies often reflect early signals of economic shifts, making them ideal vehicles for tactical exposure.'
      }
    ]
  },
  CHALLENGES: {
    TITLE: 'Challenges in Trading Major Currencies',
    ITEMS: [
      {
        TEXT: '**Asynchronous monetary policies**: Divergence between central banks (e.g., Fed vs. ECB or BoJ) can create high volatility and structural shifts.'
      },
      {
        TEXT: '**Event risk and correlation traps**: Political surprises, rate announcements, and risk-on/risk-off sentiment can cause unexpected reversals or alignments across multiple pairs.'
      }
    ]
  },
  SOLUTION: {
    TITLE: 'The Merin Solution',
    INTRO:
      'Merin is a multi-currency trading system that targets strategic opportunities across the seven majors using advanced quantitative models. The system allocates dynamically between currency pairs and directions, guided by machine learning insights, volatility regimes, and macro filters.',
    ITEMS: [
      {
        TEXT: '**Cross-pair risk balancing**: Risk exposure is continuously adjusted to account for correlation shifts and volatility clustering, ensuring controlled drawdowns.'
      },
      {
        TEXT: '**Systematic alpha extraction**: The strategy identifies reversion, breakout and momentum patterns across multiple timeframes and currencies with no discretionary overrides.'
      },
      {
        TEXT: '**Macro-aware learning engine**: The model incorporates macro data feeds and price behavior to better anticipate regime changes and reallocate capital accordingly.'
      }
    ]
  },
  OBJECTIVE: {
    TITLE: 'Objective?',
    TEXT: 'Merin seeks to deliver strong risk-adjusted returns across market cycles by tapping into the structural inefficiencies of the major currency space. In periods of instability or divergence between economies, Merin thrives by adapting quickly, redistributing exposure, and preserving capital, while capturing directional or relative-value opportunities.'
  }
};

export const DRAVON_CONTENT = {
  TITLE: 'Macro Intelligence, Tactical Precision',
  OVERVIEW: [
    'In an increasingly interconnected and reactive global economy, the ability to navigate across asset classes is key. Dravon, a macro-driven strategy by MarQ Capital, operates with a focused set of instruments – EURUSD, Gold, Crude Oil, and the S&P 500 – to extract directional and relative-value opportunities across currencies, commodities and equities.',
    'Built on a foundation of quantitative macro research and adaptive risk management, Dravon captures price inefficiencies and momentum shifts triggered by inflation cycles, monetary policy divergence, geopolitical shocks and systemic rotations in global markets.'
  ],
  WHY_MACRO_EXPOSURE: {
    TITLE: 'Why Macro Exposure?',
    ITEMS: [
      {
        TEXT: '**Cross-asset balance**: Combining currencies, commodities and indices allows Dravon to benefit from capital flows between asset classes during different market regimes.'
      },
      {
        TEXT: '**Highly responsive instruments**: EURUSD, Gold, Oil and S&P 500 react directly to macro developments, offering clear signals for tactical deployment.'
      },
      {
        TEXT: '**Strategic diversification**: Exposure to uncorrelated drivers (e.g., monetary policy, inflation, energy supply, and equity risk sentiment) enhances portfolio robustness.'
      }
    ]
  },
  CHALLENGES: {
    TITLE: 'Challenges in Macro Trading',
    ITEMS: [
      {
        TEXT: '**Volatility shocks**: Macro assets often experience violent re-pricing due to unexpected policy shifts, wars, or liquidity squeezes.'
      },
      {
        TEXT: '**Complex interdependencies**: Relationships between assets are non-linear and regime-dependent, requiring flexible models and fast adaptation.'
      }
    ]
  },
  SOLUTION: {
    TITLE: 'The Dravon Solution',
    INTRO:
      'Dravon is a tactical macro strategy designed to exploit directional and reverse opportunities across four key instruments. The system uses a multi-layered engine that combines macroeconomic data analysis, trend detection, volatility modeling and sentiment shifts to allocate capital dynamically.',
    ITEMS: [
      {
        TEXT: '**Adaptive exposure**: Position sizing is adjusted based on volatility expectations and macro regime classification, reducing exposure during turbulent transitions.'
      },
      {
        TEXT: '**Cross-asset pattern recognition**: The system identifies leading and lagging relationships between the EUR, Gold, Oil and US equities to position ahead of broader shifts.'
      },
      {
        TEXT: "**Macro-aware machine learning**: Dravon's core models evolve with incoming data, learning how each asset behaves under different combinations of inflation, interest rates and global risk sentiment."
      }
    ]
  },
  OBJECTIVE: {
    TITLE: 'Objective?',
    TEXT: 'Dravon aims to deliver asymmetric returns while limiting downside in unstable conditions. Its strength lies in its ability to respond swiftly to global events, rotate exposure across uncorrelated macro assets, and maintain a robust positioning profile during both expansion and crisis periods.'
  }
};

export const GLOBAL_MACRO_CONTENT = {
  TITLE: 'Global Thinking, Equity Precision',
  OVERVIEW: [
    'In a world shaped by inflation shocks, interest rate divergence, geopolitical conflict, deglobalization pressures and structural shifts in capital flows, equity investing demands more than passive exposure. It demands a global perspective on macro forces and the ability to translate them into precise equity positioning.',
    'Global Equity Macro, a systematic strategy by MarQ Capital, is designed to do exactly that. The strategy allocates risk exclusively across a curated set of the worlds most liquid and influential equity indices, expressing macroeconomic views through equity markets rather than across asset classes.',
    'This is not traditional equity diversification. It is macro-driven equity conviction, powered by data, disciplined risk management and adaptive learning.'
  ],
  FRAGMENTED_WORLD: {
    TITLE: 'A Fragmented Macro World, Reflected in Equities',
    INTRO:
      "The global economic environment has fundamentally changed. The era of synchronised growth, predictable liquidity cycles and stable correlations is behind us. Today's equity markets are shaped by:",
    ITEMS: [
      {
        TEXT: 'Diverging monetary policy paths across regions'
      },
      {
        TEXT: 'Persistent inflationary pressures and cost reallocation'
      },
      {
        TEXT: 'Geopolitical fragmentation and supply-chain realignment'
      },
      {
        TEXT: 'Sectoral and regional dispersion within equity markets'
      },
      {
        TEXT: 'Sharp regime shifts in volatility, liquidity and risk appetite'
      }
    ],
    CONCLUSION: [
      'Equity indices are no longer passive reflections of growth. They are macro instruments in their own right, encoding policy expectations, earnings stress, capital rotation and geopolitical risk.',
      'Global Equity Macro is built specifically for this environment.'
    ]
  },
  STRATEGIC_UNIVERSE: {
    TITLE: 'The Strategic Equity Universe',
    INTRO:
      'The strategy operates exclusively on a global basket of major equity indices, representing different economic blocs, monetary regimes and growth profiles. This universe allows the system to express:',
    ITEMS: [
      {
        TEXT: 'Relative strength and weakness between regions'
      },
      {
        TEXT: 'Risk-on versus risk-off equity dynamics'
      },
      {
        TEXT: 'Cyclical versus defensive macro positioning'
      },
      {
        TEXT: 'Stress, recovery and regime transition phases'
      }
    ],
    CONCLUSION:
      'By operating solely within equities, the strategy achieves clarity of exposure while retaining macro depth.'
  },
  CHALLENGES: {
    TITLE: 'Key Challenges in Macro-Driven Equity Investing',
    INTRO:
      'Macro equity strategies face distinct challenges that traditional equity models often fail to address:',
    ITEMS: [
      {
        TITLE: 'Regime-dependent behaviour',
        TEXT: 'Equity indices respond differently to inflation, rates and liquidity depending on the macro phase. What supports equities in one regime can destabilise them in another.'
      },
      {
        TITLE: 'Changing correlations within equities',
        TEXT: 'Regional indices can decouple sharply, driven by policy divergence, currency effects and capital reallocation.'
      },
      {
        TITLE: 'Volatility clustering and drawdown risk',
        TEXT: 'Macro shocks tend to propagate through equities rapidly, requiring dynamic risk control rather than static allocation.'
      }
    ],
    CONCLUSION:
      'Global Equity Macro is engineered to operate within these constraints, not despite them.'
  },
  SOLUTION: {
    TITLE: 'The MarQ Capital Global Equity Macro Approach',
    INTRO:
      'The strategy employs a multi-layered, systematic framework tailored specifically to equity markets:',
    ITEMS: [
      {
        TITLE: 'Macro-regime classification',
        TEXT: 'Equity exposure is continuously adjusted based on identified macro regimes such as tightening cycles, liquidity expansion, stagflation risk or recessionary stress.'
      },
      {
        TITLE: 'Machine learning with equity context',
        TEXT: 'Proprietary models process macro indicators, index-level price action, volatility structures and cross-market signals to detect regime transitions and asymmetric opportunities.'
      },
      {
        TITLE: 'Dynamic equity allocation',
        TEXT: 'The system shifts exposure between indices, adjusts position sizing and adapts risk thresholds as macro conditions evolve.'
      },
      {
        TITLE: 'Downside-aware construction',
        TEXT: 'Risk is actively managed through volatility-aware positioning, drawdown controls and exposure compression during periods of macro instability.'
      }
    ],
    CONCLUSION:
      'This is not stock picking. It is macro intelligence applied directly to equity indices.'
  },
  OBJECTIVE: {
    TITLE: 'Objective',
    INTRO:
      'To deliver asymmetric returns through macro-driven equity allocation, while actively managing downside risk during periods of heightened uncertainty.',
    BODY: [
      "Global Equity Macro is designed to capture equity inflection points, exploit regional and cyclical dispersion, and adapt continuously to evolving macro conditions. Whether navigating monetary tightening, growth slowdowns, inflation shocks or policy pivots, the strategy represents MarQ Capital's focused, disciplined and forward-looking approach to equity markets.",
      'Precision, protection and performance, expressed through global equities.'
    ]
  }
};
